import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { GripVertical, Trash2, RefreshCw, ArrowLeft } from 'lucide-react';

const API = API_URL;

const EpisodePageManager = () => {
  const { seriesId, chapterId } = useParams();
  const navigate = useNavigate();
  const [chapter, setChapter] = useState(null);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [replacingIndex, setReplacingIndex] = useState(null);
  const fileInputRefs = useRef([]);

  const fetchChapter = async () => {
    try {
      const response = await axios.get(`${API}/chapters/${chapterId}`);
      const chapterData = response.data;
      setChapter(chapterData);
      setPages(Array.isArray(chapterData.pages_data) ? chapterData.pages_data : []);
    } catch (error) {
      console.error('Error fetching chapter:', error);
      toast.error('Failed to load episode pages');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chapterId) {
      fetchChapter();
    }
  }, [chapterId]);

  const handleFileInput = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file || index == null) return;

    setReplacingIndex(index);

    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/upload/episode-page`, formData);

      const newUrl = response.data?.url;
      if (!newUrl) {
        throw new Error('Upload did not return a URL');
      }

      setPages(prev => {
        const next = [...prev];
        const existing = next[index] || {};
        next[index] = { ...existing, url: newUrl };
        return next;
      });
      toast.success('Page replaced');
    } catch (error) {
      console.error('Error replacing page:', error);
      const detail = error.response?.data?.detail || error.message || 'Failed to replace page';
      toast.error(detail);
    } finally {
      setReplacingIndex(null);
      event.target.value = '';
    }
  };

  const handleDeletePage = (index) => {
    if (pages.length <= 1) {
      toast.error('An episode must have at least 1 page. If you want to hide this episode, use Unpublish instead.');
      return;
    }

    const confirmed = window.confirm('Delete this page? This cannot be undone.');
    if (!confirmed) return;

    setPages(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragStart = (event, index) => {
    setDraggedIndex(index);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (event, index) => {
    event.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setPages(prev => {
      const updated = [...prev];
      const [removed] = updated.splice(draggedIndex, 1);
      updated.splice(index, 0, removed);
      setDraggedIndex(index);
      return updated;
    });
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSave = async () => {
    if (!chapter) return;
    if (!Array.isArray(pages) || pages.length === 0) {
      toast.error('Episode must contain at least one page.');
      return;
    }

    setSaving(true);
    try {
      await axios.patch(`${API}/chapters/${chapterId}/pages`, {
        pages_data: pages,
      });
      toast.success('Episode pages updated');
      navigate(`/series/${seriesId}/read/${chapterId}`);
    } catch (error) {
      console.error('Error saving pages:', error);
      const detail = error.response?.data?.detail || 'Failed to update episode pages';
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  const handleAddPage = async () => {
    try {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/jpg,image/png,image/webp';
      // iPad Safari requires the input to be in the DOM before .click()
      input.style.position = 'fixed';
      input.style.top = '-1000px';
      input.style.left = '-1000px';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.onchange = async (event) => {
        const file = event.target.files?.[0];
        document.body.removeChild(input);
        if (!file) return;

        setSaving(true);
        try {
          const formData = new FormData();
          formData.append('file', file);
          const response = await axios.post(`${API}/upload/episode-page`, formData);
          const newUrl = response.data?.url;
          if (!newUrl) {
            throw new Error('Upload did not return a URL');
          }
          setPages(prev => [...prev, { url: newUrl }]);
          toast.success('Page added');
        } catch (error) {
          console.error('Error adding page:', error);
          const detail = error.response?.data?.detail || error.message || 'Failed to add page';
          toast.error(detail);
        } finally {
          setSaving(false);
        }
      };
      input.click();
    } catch (error) {
      console.error('Error preparing file input:', error);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <div className="text-xl font-medium">Loading episode pages...</div>
        </div>
      </div>
    );
  }

  if (!chapter) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Episode not found</h2>
          <Link to={`/series/${seriesId}`}>
            <Button className="bg-orange-500 hover:bg-orange-600">Back to Series</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="border-b border-gray-800 bg-gray-900/80 backdrop-blur">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              size="sm"
              className="text-gray-300 hover:text-white"
              onClick={() => navigate(-1)}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wide">Manage Pages</p>
              <h1 className="text-lg font-bold">{chapter.title}</h1>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-700 disabled:cursor-not-allowed text-white"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-6 py-8">
        <Card className="bg-gray-900 border-gray-800">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <CardTitle className="text-xl">Episode Pages</CardTitle>
            <Button
              type="button"
              variant="outline"
              onClick={handleAddPage}
              disabled={saving}
              className="border-orange-500 text-orange-400 hover:bg-orange-500 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Page
            </Button>
          </CardHeader>
          <CardContent>
            <div className="mb-4 text-sm text-gray-400">
              Editing pages does not reset views or stats.
            </div>
            {pages.length === 0 ? (
              <p className="text-gray-400 text-sm">No pages found for this episode.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                {pages.map((page, index) => (
                  <div
                    key={index}
                    draggable
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`relative bg-gray-800 border-2 rounded-lg overflow-hidden group cursor-move ${
                      draggedIndex === index ? 'border-orange-500 opacity-75' : 'border-gray-700'
                    }`}
                  >
                    <div className="absolute top-2 left-2 z-10 bg-black/70 rounded p-1 flex items-center space-x-1">
                      <GripVertical className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-300">Page {index + 1}</span>
                    </div>

                    <div className="absolute top-2 right-2 z-10 flex space-x-1">
                      <label className="bg-gray-900/80 hover:bg-gray-800 text-gray-200 rounded-full p-1 cursor-pointer transition-colors">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp"
                          className="sr-only"
                          onChange={(e) => handleFileInput(e, index)}
                        />
                        <RefreshCw className="w-4 h-4" />
                      </label>
                      <button
                        type="button"
                        onClick={() => handleDeletePage(index)}
                        className="bg-red-600/90 hover:bg-red-700 text-white rounded-full p-1 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="aspect-[3/4] bg-black">
                      {page?.url ? (
                        <img
                          src={page.url}
                          alt={`Page ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">
                          Missing page URL
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p className="mt-4 text-xs text-gray-500">
              Tip: Drag and drop pages to reorder them. Replacing or deleting pages does not reset episode views or stats.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EpisodePageManager;
